import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled, { StylesProps } from '../../../lib/styles';
import articleReelBlockStyles from './ArticleReelBlock_styles';
import { compose } from 'recompose';
import { ArticleItem } from '../../../lib/algolia/types';
import { ArticleTileItem } from '../ArticleTileBlock/ArticleTileBlock';

/**
 * Props for the component.
 */
export interface ArticleReelBlockProps {
  articles: ArticleItem[];
}

const INTERVAL_TIME = 5000; // Milliseconds for auto-slide interval

/**
 * Component to render a block of articles in a reel style.
 * @param {ArticleReelBlockProps} props - The properties for the component.
 * @returns JSX element representing the block.
 */
const ArticleReelBlock: React.FC<ArticleReelBlockProps> = ({
  articles,
}: ArticleReelBlockProps & StylesProps<typeof articleReelBlockStyles>) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0); // New state for progress bar
  const intervalRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null); // Ref for progress animation interval

  //  DEBUGGING
	// useEffect(() => {
	// 	console.log('ArticleReelBlock mounted', { articles, styles: articleReelBlockStyles });
	// }, [articleReelBlockStyles]);

  const goToNextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % articles.length);
    setProgress(0); // Reset progress when slide changes
  }, [articles.length]);

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(goToNextSlide, INTERVAL_TIME);
  }, [goToNextSlide]);

  const startProgressAnimation = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    const startTime = Date.now();
    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(100, (elapsed / INTERVAL_TIME) * 100);
      setProgress(newProgress);
      if (newProgress >= 100) {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      }
    }, 50); // Update progress every 50ms for a smooth animation
  }, []);

  // Effect for automatic sliding and progress animation
  useEffect(() => {
    if (articles.length > 1) {
      startAutoSlide();
      startProgressAnimation(); // Start progress animation when auto-slide starts
    }

    // Cleanup function to clear intervals when the component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [articles.length, startAutoSlide, startProgressAnimation]); // Re-run effect if these functions change

  // Effect to reset progress and start animation when currentIndex changes
  useEffect(() => {
    if (articles.length > 1) {
      setProgress(0); // Reset progress for the new slide
      startProgressAnimation();
    }
  }, [currentIndex, articles.length, startProgressAnimation]);

  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
    // Reset and restart both timers when an indicator is clicked
    startAutoSlide();
    setProgress(0); // Reset progress immediately on click
    startProgressAnimation();
  };

  if (!articles || articles.length === 0) {
    return null; // Don't render if there are no articles
  }

  return (
    <div style={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
      {articles.length > 1 && (
        <div className="reel-selector-row d-flex flex-nowrap align-items-center"
          style={{
            position: 'absolute',
            top: '2px',
            left: '2px',
            width: 'calc(100% - 4px)',
            zIndex: 1,
            backgroundColor: 'transparent',
            height: '5px',
            display: 'flex',
						gap: '8px',
          }}
        >
          {articles.map((article, index) => (
            <div
              key={`indicator-${article.id}`}
              onClick={() => handleIndicatorClick(index)}
              style={{
                flex: '1',
                height: '100%',
                backgroundColor: 'rgb(184, 152, 255)',
                cursor: 'pointer',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {currentIndex === index && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${progress}%`, // Progress bar width
                    backgroundColor: '#fff', // White for active/progress
                    transition: 'width 0.05s linear', // Smooth progress animation
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Article Reel - Inner container for sliding effect */}
      <div style={{
        display: 'flex',
        transition: 'transform 0.5s ease-in-out',
        transform: `translateX(-${currentIndex * 100}%)`,
        width: `100%`,
      }}>
        {articles.map((article) => (
          <div
            key={article.id}
            style={{
              flex: '0 0 100%',
              width: '100%',
            }}
          >
            <ArticleTileItem
              article={article}
              options={{
                textPosition: 'bottom',
                tileWidth: '100%',
                isCoverImage: true,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default compose(styled(articleReelBlockStyles))(ArticleReelBlock);